/**
 * Bootstrap Components Initializer
 *
 * Fixes issues with Bootstrap components (collapse, dropdown, etc.) when navigating
 * back/forward with Turbo. Ensures component state is properly reset and event
 * handlers are correctly initialized.
 */

class BootstrapComponentsHandler {
  constructor() {
    this.init();
  }

  init() {
    // Reset component states on turbo:before-cache to prevent stale states
    document.addEventListener('turbo:before-cache', () => {
      this.resetComponentStates();
    });

    // Reinitialize components after turbo loads
    document.addEventListener('turbo:load', () => {
      this.initializeComponents();
    });
  }

  resetComponentStates() {
    this.resetCollapseStates();
    this.resetDropdownStates();
    this.resetOffcanvasStates();
  }

  resetCollapseStates() {
    // Find all collapse elements and reset their state
    document.querySelectorAll('.collapse').forEach(collapseEl => {
      // Remove 'show' class to ensure collapsed state
      collapseEl.classList.remove('show');

      // Reset chevron initialization flag
      collapseEl.removeAttribute('data-bs-collapse-chevron-init');

      // Reset aria-expanded on trigger buttons
      const triggerSelector = `[data-bs-target="#${collapseEl.id}"], [href="#${collapseEl.id}"]`;
      document.querySelectorAll(triggerSelector).forEach(trigger => {
        trigger.setAttribute('aria-expanded', 'false');
        // Reset any custom classes like 'collapsed'
        if (trigger.classList.contains('accordion-button')) {
          trigger.classList.add('collapsed');
        }
      });

      // Dispose of Bootstrap Collapse instance if it exists
      if (window.bootstrap && window.bootstrap.Collapse) {
        const collapseInstance = window.bootstrap.Collapse.getInstance(collapseEl);
        if (collapseInstance) {
          collapseInstance.dispose();
        }
      }
    });
  }

  resetDropdownStates() {
    // Find all dropdown elements and reset their state
    document.querySelectorAll('.dropdown-menu').forEach(dropdownEl => {
      // Remove 'show' class to ensure closed state
      dropdownEl.classList.remove('show');

      // Reset data attributes
      dropdownEl.removeAttribute('data-bs-popper');
      dropdownEl.removeAttribute('style');
    });

    // Reset dropdown toggle buttons
    document.querySelectorAll('[data-bs-toggle="dropdown"]').forEach(toggle => {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.classList.remove('show');

      // Dispose of Bootstrap Dropdown instance if it exists
      if (window.bootstrap && window.bootstrap.Dropdown) {
        const dropdownInstance = window.bootstrap.Dropdown.getInstance(toggle);
        if (dropdownInstance) {
          dropdownInstance.dispose();
        }
      }
    });
  }

  initializeComponents() {
    this.initializeCollapseComponents();
    this.initializeDropdownComponents();
    this.initializeOffcanvasComponents();
  }

  initializeCollapseComponents() {
    // Reinitialize all collapse components
    document.querySelectorAll('.collapse').forEach(collapseEl => {
      if (window.bootstrap && window.bootstrap.Collapse) {
        // Create new Bootstrap Collapse instance
        new window.bootstrap.Collapse(collapseEl, {
          toggle: false // Don't auto-toggle on initialization
        });
      }
    });

    // Reinitialize event handlers for chevron rotation (if used)
    this.initializeChevronHandlers();
  }

  initializeDropdownComponents() {
    // Reinitialize all dropdown components
    document.querySelectorAll('[data-bs-toggle="dropdown"]').forEach(dropdownToggle => {
      if (window.bootstrap && window.bootstrap.Dropdown) {
        // Create new Bootstrap Dropdown instance
        new window.bootstrap.Dropdown(dropdownToggle);
      }
    });
  }

  initializeChevronHandlers() {
    document.querySelectorAll('[data-bs-toggle="collapse"]').forEach(button => {
      const targetSelector = button.getAttribute('data-bs-target') || button.getAttribute('href');
      if (!targetSelector) return;

      const target = document.querySelector(targetSelector);
      if (target) {
        // Use a flag to prevent duplicate event listeners
        if (target.hasAttribute('data-bs-collapse-chevron-init')) return;
        target.setAttribute('data-bs-collapse-chevron-init', 'true');

        // Add fresh event listeners
        target.addEventListener('show.bs.collapse', () => {
          const chevron = button.querySelector('[class*="chevron"]');
          if (chevron) {
            chevron.style.transform = 'rotate(180deg)';
            chevron.style.transition = 'transform 0.2s ease';
          }
        });

        target.addEventListener('hide.bs.collapse', () => {
          const chevron = button.querySelector('[class*="chevron"]');
          if (chevron) {
            chevron.style.transform = 'rotate(0deg)';
            chevron.style.transition = 'transform 0.2s ease';
          }
        });
      }
    });
  }

  resetOffcanvasStates() {
    // Find all offcanvas elements (including responsive variants like offcanvas-lg)
    document.querySelectorAll('[class*="offcanvas"]').forEach(offcanvasEl => {
      // Only process actual offcanvas elements (not just elements with "offcanvas" in class name)
      if (!offcanvasEl.classList.contains('offcanvas') &&
          !offcanvasEl.className.match(/offcanvas-(sm|md|lg|xl|xxl)/)) {
        return;
      }

      // Remove 'show' class to ensure closed state
      offcanvasEl.classList.remove('show');
      offcanvasEl.classList.remove('showing');
      offcanvasEl.classList.remove('hiding');

      // Reset aria attributes
      offcanvasEl.setAttribute('aria-hidden', 'true');
      offcanvasEl.removeAttribute('aria-modal');
      offcanvasEl.removeAttribute('role');
      offcanvasEl.removeAttribute('style');

      // Dispose of Bootstrap Offcanvas instance if it exists
      if (window.bootstrap && window.bootstrap.Offcanvas) {
        const offcanvasInstance = window.bootstrap.Offcanvas.getInstance(offcanvasEl);
        if (offcanvasInstance) {
          offcanvasInstance.dispose();
        }
      }
    });

    // Reset offcanvas toggle buttons
    document.querySelectorAll('[data-bs-toggle="offcanvas"]').forEach(toggle => {
      toggle.setAttribute('aria-expanded', 'false');
    });

    // Remove any lingering backdrops
    document.querySelectorAll('.offcanvas-backdrop').forEach(backdrop => {
      backdrop.remove();
    });

    // Clean up body classes and styles
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  initializeOffcanvasComponents() {
    // Reinitialize all offcanvas components (including responsive variants like offcanvas-lg)
    document.querySelectorAll('[class*="offcanvas"]').forEach(offcanvasEl => {
      // Only process actual offcanvas elements
      if (!offcanvasEl.classList.contains('offcanvas') &&
          !offcanvasEl.className.match(/offcanvas-(sm|md|lg|xl|xxl)/)) {
        return;
      }

      if (window.bootstrap && window.bootstrap.Offcanvas) {
        // Create new Bootstrap Offcanvas instance
        new window.bootstrap.Offcanvas(offcanvasEl, {
          toggle: false // Don't auto-toggle on initialization
        });
      }
    });
  }
}

// Initialize the handler
new BootstrapComponentsHandler();